import {
  GetUnreadThreadsResponse,
  InboxMessage,
  RootEntityType,
  useGetMessagesByUserTypeQuery,
  useGetUnreadThreadsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const REFRESH_INTERVAL = 40000;

type NotificationsContextValue = {
  threadsUnread: GetUnreadThreadsResponse | undefined;
  onNotificationsUpdate: (callback: () => void) => () => void;
  notificationTimestamp: number;
  isThreadsUnreadLoading: boolean;
  messages: InboxMessage[] | undefined;
  isMessagesLoading: boolean;
  invalidateNotifications: VoidFunction;
};

const defaultContext: NotificationsContextValue = {
  threadsUnread: undefined,
  onNotificationsUpdate: () => () => {},
  notificationTimestamp: 0,
  isThreadsUnreadLoading: false,
  messages: undefined,
  isMessagesLoading: false,
  invalidateNotifications: () => {},
};

const UserNotificationContext = createContext(defaultContext);

export const useUserNotifications = () => useContext(UserNotificationContext);

export const WithUserNotifications: FC<PropsWithChildren> = ({ children }) => {
  const { schoolId, currentUser } = useAuth();
  const [notificationTimestamp, setNotificationTimestamp] = useState(0);
  const updateCallbacks = React.useRef<Array<() => void>>([]);
  const invalidateQueries = useInvalidateListQueriesFor('notifications');

  const userType = useMemo(
    () =>
      Boolean(
        currentUser?.school_relations.find(
          (s) => s.role === SchoolUserRole.Staff && s.school.id === schoolId,
        ),
      )
        ? SchoolUserRole.Staff
        : SchoolUserRole.Parent,
    [currentUser?.school_relations, schoolId],
  );
  const { data: messages, isLoading: isMessagesLoading } = useGetMessagesByUserTypeQuery(userType, {
    refetchOnMount: 'always',
  });

  const handleSuccess = useCallback(() => {
    setNotificationTimestamp(Date.now());
    updateCallbacks.current.forEach((callback) => callback());
  }, []);

  const { data: threadsUnread, isLoading: isThreadsUnreadLoading } = useGetUnreadThreadsQuery(
    {
      school_id: schoolId || '',
      root_entity_type: RootEntityType.MESSAGE,
    },
    {
      enabled: !!schoolId,
      refetchInterval: REFRESH_INTERVAL,
      refetchOnMount: 'always',
      onSuccess: handleSuccess,
    },
  );

  const onNotificationsUpdate = useCallback((callback: () => void) => {
    updateCallbacks.current.push(callback);

    return () => {
      updateCallbacks.current = updateCallbacks.current.filter((cb) => cb !== callback);
    };
  }, []);

  return (
    <UserNotificationContext.Provider
      value={{
        threadsUnread,
        onNotificationsUpdate,
        notificationTimestamp,
        isThreadsUnreadLoading,
        messages,
        isMessagesLoading,
        invalidateNotifications: invalidateQueries,
      }}
    >
      {children}
    </UserNotificationContext.Provider>
  );
};
